import { DateTimeRangePicker } from '@optios/react-date-time-range-picker'
import moment, { Moment } from 'moment'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Person } from '../../App/store/features/person/IPerson'
import { formatDate } from '../../App/Util'
import { useKeyPress } from '../../Common/Hooks/useKeyPress'
import { useWindowDimensions } from '../../Common/Hooks/useWindowDimensions'
import { CalendarToolbarNextButtons } from '../CalendarToolbarNextButtons'
import { CalendarToolbarPreviousButtons } from '../CalendarToolbarPreviousButtons'
import { CalendarToolbarSettings } from '../CalendarToolbarSettings'
import { useOutsideClick } from '../utils/useOutsideClick'

import {
  CalendarSettingsButton,
  CalendarSettingsButtonIcon,
  CalendarTodayDateButton,
  CalendarTodayDateButtonSpan,
  CalendarTodayDateButtonText,
  CalendarTodayDateView,
  CalendarToolbarWrapper,
  DateTimeRangePickerWrapper,
} from './style'

interface IProps {
  date: Moment
  onDateChanged: (date: Moment) => void
  persons: Person[]
  amountOfDays: number
}

export const CalendarToolbar = ({
  date,
  onDateChanged,
  persons,
  amountOfDays,
}: IProps) => {
  const { windowWidth } = useWindowDimensions()
  const { t } = useTranslation()

  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false)
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  const isEscPress: boolean = useKeyPress('Escape')

  const toggleSettingsOpen = () => setIsSettingsOpen(!isSettingsOpen)
  const formattedDate = formatDate(
    date,
    windowWidth < 500 ? 'FORMAT_DATE_EXTRA_SHORT' : 'FORMAT_DATE_FULL',
  )

  const closeOnOutsideClick = () => {
    setIsSettingsOpen(false)
    setIsDatePickerOpen(false)
  }
  const settingContainerRef = useOutsideClick(closeOnOutsideClick)

  useMemo(() => {
    if (!isEscPress) {
      return
    }
    closeOnOutsideClick()
  }, [isEscPress])

  return (
    <CalendarToolbarWrapper windowWidth={windowWidth}>
      <CalendarTodayDateButton
        type="button"
        onClick={() => onDateChanged(moment())}>
        <CalendarTodayDateButtonText>{t('Today')}</CalendarTodayDateButtonText>
      </CalendarTodayDateButton>

      <div>
        <CalendarToolbarPreviousButtons
          date={date}
          onDateChanged={onDateChanged}
          amountOfDays={amountOfDays}
          windowWidth={windowWidth}
        />

        <CalendarTodayDateView
          type="button"
          onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}>
          <CalendarTodayDateButtonSpan>
            {formattedDate}
          </CalendarTodayDateButtonSpan>
        </CalendarTodayDateView>

        {isDatePickerOpen && (
          <DateTimeRangePickerWrapper ref={settingContainerRef}>
            <DateTimeRangePicker
              onChange={fromDate => {
                onDateChanged(fromDate)
                setIsDatePickerOpen(false)
              }}
            />
          </DateTimeRangePickerWrapper>
        )}
        <CalendarToolbarNextButtons
          date={date}
          onDateChanged={onDateChanged}
          amountOfDays={amountOfDays}
          windowWidth={windowWidth}
        />
      </div>
      <CalendarSettingsButton onClick={toggleSettingsOpen}>
        <CalendarSettingsButtonIcon icon="cog" />
      </CalendarSettingsButton>

      <CalendarToolbarSettings
        isSettingsOpen={isSettingsOpen}
        persons={persons}
        settingContainerRef={settingContainerRef}
      />
    </CalendarToolbarWrapper>
  )
}
